/**
 * অবস্থা ৬ · যাচাই অপেক্ষমাণ — `P0 States § ৯` (Pending · Verification).
 * `E_VERIFICATION_PENDING`.
 *
 * Frame note: "Two ticks and one cross: most of the app still works, one thing does
 * not. A pending state that only says 'please wait' sends the user to a competitor
 * while they wait."
 *
 * § অপেক্ষা ও প্রত্যাখ্যান states the rule this frame implements: a waiting state must
 * carry two things — how long it takes, and what can be done meanwhile.
 *
 * W1-04 · BOTH OF THOSE ARE NOW THE SERVER'S TO SAY.
 * This is the frame a deferred session meets when it tries to order, pay, apply as a
 * vendor or ask for a refund (founder decision 2026-09-18: deferred sessions browse but do
 * not transact). The drawn frame answered it with somebody else's case, as fact:
 *
 *   · the header অপ্রাপ্য ক্যাশ — the screen § ৯ was drawn on, not the one the person is on;
 *   · "আজ দুপুর ২টার আগে শেষ হওয়ার কথা" — a time nobody promised;
 *   · a three-step document rail stamped "গতকাল ৪:২০" — for a check that is not about
 *     documents at all, but about a phone number the OTP channel could not reach;
 *   · "meanwhile you can order by card or by hand, and every bill and recharge is on" —
 *     to a session that has just been refused an order;
 *   · "you will be told when it is done" — a notification nothing sends.
 *
 * So the frame keeps its heading, its chip and its card, and the card carries
 * `message_bn` — the server knows how long, and what still works — or nothing. The
 * header carries `header_bn` when the server names the place, and is otherwise just the
 * back control. Recorded in `docs/deviations/W1-04.md` (S9).
 *
 * `#B07A16` on the card border is the second half of the off-palette warning pair.
 * Transcribed and flagged — see chrome.ts § WARNING_PANEL.
 */
import { ariaLabel } from '@oprappo/tokens';
import { StateFrame, WARNING_BORDER, BackChevron } from './chrome.js';
import { VERIFICATION_PENDING } from './data.js';

export interface VerificationPendingStateProps {
  /** `error.message_bn` — how long, and what still works. Absent or empty: not drawn. */
  bodyBn?: string | undefined;
  /** `header_bn` from the refusal — where this happened. Absent: no title in the bar. */
  headerBn?: string | undefined;
  onBack?: (() => void) | undefined;
}

function said(value: string | undefined): value is string {
  return value !== undefined && value.trim() !== '';
}

export function VerificationPendingState({
  bodyBn,
  headerBn,
  onBack,
}: VerificationPendingStateProps = {}): JSX.Element {
  const d = VERIFICATION_PENDING;
  return (
    <StateFrame stateKey="verification_pending">
      <div className="bg-white border-b border-border px-[16px] py-[14px] flex gap-[12px] items-center">
        <button
          type="button"
          aria-label={ariaLabel.back}
          onClick={onBack}
          className="bg-transparent border-0 p-0 rounded-none flex-none"
        >
          <BackChevron />
        </button>
        {said(headerBn) ? (
          <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{headerBn}</div>
        ) : null}
      </div>

      <div className="flex-1 p-[16px] oprappo-scroll">
        <div role="status" className={'bg-white border ' + WARNING_BORDER + ' border-l-[3px] border-l-amber p-[15px]'}>
          <div className="flex justify-between items-start gap-[11px]">
            <div className="font-anek font-semibold text-[18px] leading-[1.75] text-ink">{d.title_bn}</div>
            <div className="border border-amber-text px-[8px] py-[2px] font-anek font-medium text-[13px] leading-[1.75] text-amber-text flex-none">
              {d.chip_bn}
            </div>
          </div>
          {/* How long it takes, and what still works — named by the server, not "soon". */}
          {said(bodyBn) ? (
            <div className="font-anek font-normal text-[14.5px] leading-[1.75] text-ink2 mt-[8px]">{bodyBn}</div>
          ) : null}
        </div>
      </div>
    </StateFrame>
  );
}
